import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    Body1,
    Button,
    Center,
    Modal,
    Stack,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import type { OnHoldConfig } from '../../../../configuration/shared';
import { justPlacedOnHoldVar } from '../../../../contexts/fill';
import { useFillPathway } from '../../../../contexts/fill/use-fill-prescription';
import { RequestPrescriberOrderModificationDocument } from '../../../../graphql/generated/graphql';
import { usePatientContext } from '../../../../hooks/use-patient-context';
import { useRefetchQueries } from '../../../../hooks/use-refetch-queries';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';
import { getRefetchQueryForPrescription } from '../../../../util/get-refetch-query-for-prescription';

import { usePatientChoiceMutation } from './use-patient-choice-mutation';

export type PlaceOnHoldButtonProps = {
    onHoldConfig: OnHoldConfig;
    prescriberOrderId: string;
};

export const PlaceOnHoldButton = ({
    onHoldConfig,
    prescriberOrderId,
}: PlaceOnHoldButtonProps) => {
    const [showHoldModal, { close, toggle }] = useDisclosure(false);
    const navigate = useNavigate();
    const { patient: userPatient } = usePatientContext();
    const { pathway, originalPathway, subPathway } = useFillPathway();

    const savePatientChoices = usePatientChoiceMutation({
        drxId: prescriberOrderId,
    });

    const [requestModification, { loading }] = useMutation(
        RequestPrescriberOrderModificationDocument
    );

    const {
        refetch,
        isComplete: isRefetchComplete,
        isLoading: isRefetchLoading,
    } = useRefetchQueries(
        getRefetchQueryForPrescription(prescriberOrderId, userPatient.id)
    );

    const handleConfirm = async () => {
        savePatientChoices({ pathway, originalPathway, subPathway });

        const result = await requestModification({
            variables: {
                prescriberOrderId,
                putPrescriptionOnHold: true,
                preventAutoRoute: true,
            },
        });

        if (result.data?.requestPrescriberOrderModification.success) {
            justPlacedOnHoldVar(true);
            void refetch();
        }
    };

    useEffect(() => {
        if (isRefetchComplete) {
            navigate({
                pathname: getAbsoluteRoute('fill.drxId.onHold', {
                    drxId: prescriberOrderId,
                }),
            });
        }
    }, [isRefetchComplete, navigate, prescriberOrderId]);

    return (
        <>
            <Center>
                <Button
                    variant="transparent"
                    onClick={toggle}
                    data-testid="pharmacy-preview-see-other-options-button"
                >
                    {onHoldConfig.buttonText}
                </Button>
            </Center>

            <Modal
                opened={showHoldModal}
                onClose={toggle}
                title={onHoldConfig.modal.title}
                data-testid="pharmacy-preview-options-modal"
            >
                <Stack gap="xxl">
                    <Stack gap="xs">
                        {onHoldConfig.modal.body.map((line) => (
                            <Body1 key={line}>
                                <Trans
                                    ns="dynamic"
                                    components={{ b: <strong />, br: <br /> }}
                                >
                                    {line}
                                </Trans>
                            </Body1>
                        ))}
                    </Stack>
                    <Stack>
                        <Button
                            loading={loading || isRefetchLoading}
                            onClick={handleConfirm}
                            data-testid="pharmacy-preview-options-modal-confirm-and-continue-button"
                        >
                            {onHoldConfig.modal.buttonLabels.confirm}
                        </Button>
                        <Button
                            disabled={loading || isRefetchLoading}
                            variant="outline"
                            onClick={close}
                            data-testid="pharmacy-preview-options-modal-close-button"
                        >
                            {onHoldConfig.modal.buttonLabels.close}
                        </Button>
                    </Stack>
                </Stack>
            </Modal>
        </>
    );
};
